/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies */
import Model, {
  attr,
  belongsTo,
  type AsyncBelongsTo,
} from '@warp-drive/legacy/model';
import { computed } from '@ember/object';
import { filterBy, filter, alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import type { Type } from '@warp-drive/core/types/symbols';
import { fragment } from 'ember-data-model-fragments/attributes';
import type DexResponse from 'linced-app/models/enrolments/nz-moe/dex-response';
import type EnrolError from 'linced-app/models/enrolments/nz-moe/enrol-error';
import type EnrolLearner from 'linced-app/models/enrolments/nz-moe/enrol-learner';
import type Status from 'linced-app/models/enrolments/status';
import type Enrolment from 'linced-app/models/people/enrolment';
import type Person from 'linced-app/models/person';
import type School from 'linced-app/models/school';

// Difference is one row of differences: a Hero value and its ENROL value.
interface Difference {
  field: string;
  key: string;
  linced: string;
  enrol: string | undefined;
  different: boolean;
}

// Proto: none. This is the base class of enrolments.Enrolment and
// enrolments.Unenrolment, which have the same fields.
export default class BaseEnrolmentUnenrolment extends Model {
  // Subclasses in linced-app extend this class with their own types.
  declare [Type]: string;

  @attr('number')
  declare createTime: number | undefined;

  @attr('number')
  declare updateTime: number | undefined;

  @belongsTo('school', { async: true, inverse: null })
  declare schoolId: AsyncBelongsTo<School>;

  @alias('schoolId')
  declare school: AsyncBelongsTo<School>;

  @attr('string', { defaultValue: '' })
  declare campusId: string; // TODO: implement when we have Campuses

  @belongsTo('enrolments/status', { async: true, inverse: null })
  declare statusId: AsyncBelongsTo<Status>;

  @alias('statusId')
  declare status: AsyncBelongsTo<Status>;

  @belongsTo('person', { async: true, inverse: null })
  declare requesterId: AsyncBelongsTo<Person>;

  @alias('requesterId')
  declare requester: AsyncBelongsTo<Person>;

  @attr('number', { defaultValue: 0 })
  declare jurisdiction: number;

  @belongsTo('person', { async: true, inverse: null })
  declare personId: AsyncBelongsTo<Person>;

  @alias('personId')
  declare person: AsyncBelongsTo<Person>;

  @belongsTo('people/enrolment', { async: true, inverse: null })
  declare personEnrolmentId: AsyncBelongsTo<Enrolment>;

  @alias('personEnrolmentId')
  declare personEnrolment: AsyncBelongsTo<Enrolment>;

  @fragment('enrolments/nz-moe/dex-response')
  declare nzMoe: DexResponse | null;

  // Computed properties to make actual result status determination easier.
  @alias('nzMoe.type')
  declare type: string | undefined;

  @filterBy('nzMoe.errors', 'code', 51)
  declare generalErrors: EnrolError[];

  @filterBy('nzMoe.errors', 'code', 52)
  declare structuralErrors: EnrolError[];

  @filterBy('nzMoe.errors', 'code', 53)
  declare validationErrors: EnrolError[];

  @filterBy('nzMoe.errors', 'code', 54)
  declare businessRuleErrors: EnrolError[];

  @filter('nzMoe.errors', function (err) {
    return ![51, 52, 53, 54].includes((err as EnrolError).code);
  })
  declare otherDexErrors: EnrolError[];

  // differences provides a list of differences between Hero and ENROL
  // for the learner in question, along with names for identification.
  @computed(
    'person.fields.{dob,nzMOE:gender.value}',
    'nzMoe.learners.firstObject.{birthdate,gender}',
  )
  get differences() {
    const differences: Difference[] = [];
    // The isEmpty check below guards the undefined case.
    const result = this.get('nzMoe.learners.firstObject') as EnrolLearner;
    if (isEmpty(result)) {
      return differences;
    }
    const existingDob = this.get('person.dob') as string;
    const resultDob = result.get('birthdate');
    if (!isEmpty(resultDob)) {
      differences.push({
        field: 'Date of birth',
        key: 'core:dob',
        linced: existingDob,
        enrol: resultDob,
        different: existingDob !== resultDob,
      });
    }
    const moeGender = this.person.get('fields.nzMOE:gender.value') as string;
    const resultGender = result.get('gender');
    if (!isEmpty(resultGender)) {
      differences.push({
        field: 'Gender',
        key: 'nzMOE:gender',
        linced: moeGender,
        enrol: resultGender,
        different: moeGender !== resultGender,
      });
    }

    // Names are returned, and should be shown (if different)
    // to aid in determining if it's the same student.
    const rawFirstName =
      (this.person.get('fields.core:firstNameLegal.value') as
        string | undefined) ?? '';
    const firstName = rawFirstName.trim().toLowerCase();
    const middle = (
      (this.person.get('fields.core:middleNames.value') as
        string | undefined) ?? ''
    )
      .trim()
      .toLowerCase();
    const firstNames = `${firstName} ${middle}`.trim();
    const rawResultFirstName = result.get('firstName');
    let resultFirstName = '';
    if (!isEmpty(rawResultFirstName)) {
      resultFirstName = rawResultFirstName.trim().toLowerCase();
    }
    if (!isEmpty(resultFirstName)) {
      differences.push({
        field: 'First Name(s)',
        key: 'core:firstNameLegal',
        linced: rawFirstName,
        enrol: result.get('firstName').trim(),
        different:
          resultFirstName !== firstName && resultFirstName !== firstNames,
      });
    }

    const rawLastName =
      (this.person.get('fields.core:lastNameLegal.value') as
        string | undefined) ?? '';
    const lastName = rawLastName.trim().toLowerCase();
    const rawResultLastName = result.get('lastName');
    let resultLastName = '';
    if (!isEmpty(rawResultLastName)) {
      resultLastName = rawResultLastName.trim().toLowerCase();
    }
    if (!isEmpty(resultLastName)) {
      differences.push({
        field: 'Last Name',
        key: 'core:lastNameLegal',
        linced: rawLastName,
        enrol: result.get('lastName').trim(),
        different: resultLastName !== lastName,
      });
    }

    return differences;
  }

  // statusDescription provides a longer plain text description of the upload
  // status, i.e. for use in the printable bulk upload report.
  @computed(
    'type',
    'generalErrors.[]',
    'structuralErrors.[]',
    'validationErrors.[]',
    'businessRuleErrors.[]',
    'otherDexErrors.[]',
    'differences.@each.different',
    'school.name',
  )
  get statusDescription() {
    let description = 'Unknown error';
    let result: EnrolLearner;
    switch (this.type) {
      case 'A':
        switch (true) {
          case this.generalErrors.length > 0:
            description = 'ENROL is unavailable.  Please try again later.';
            break;
          case this.structuralErrors.length > 0:
            description =
              'Communication error.  Please contact the Hero helpdesk for assistance.';
            break;
          case this.validationErrors.length > 0:
            description = this.validationErrors
              .reduce(
                (parts, err) => {
                  parts.push(err.message);
                  return parts;
                },
                ['Data validation error(s).'],
              )
              .join(' ');
            break;
          case this.businessRuleErrors.length > 0:
            description = this.businessRuleErrors
              .reduce(
                (parts, err) => {
                  parts.push(err.message);
                  return parts;
                },
                ['Business rule validation error(s).'],
              )
              .join(' ');
            break;
          case this.otherDexErrors.length > 0:
            description = this.otherDexErrors
              .reduce(
                (parts, err) => {
                  parts.push(err.message);
                  return parts;
                },
                ['Error'],
              )
              .join(' ');
            break;
        }
        break;

      case 'G':
        description =
          'NSN not found in enrol, and has been removed.  Perform a Get/Check NSN to retrieve the correct NSN.';
        break;

      case 'H':
        description = this.differences
          .reduce(
            (parts, diff) => {
              if (diff.different) {
                parts.push(
                  `${diff.field} is different (${diff.linced} / ${diff.enrol}).`,
                );
              }
              return parts;
            },
            ['Data mismatch.'],
          )
          .join(' ');
        break;

      // Type I response is for leaver uploads only.
      case 'I':
        description = `Not currently enrolled at ${
          this.get('school.name') as string
        }.  No further action is required.`;
        break;

      // Type P response is for arrival uploads only.
      case 'P':
        description =
          'First day of attendance is invalid in relation to a previous enrolment.';
        // The isEmpty check below guards the undefined case.
        result = this.get('nzMoe.learners.firstObject') as EnrolLearner;
        if (!isEmpty(result)) {
          if (result.isConfidential) {
            description += `Due to special circumstances, details can't be shown.`;
          } else if (!isEmpty(result.lastAttendanceDate)) {
            description += `Last attended ${result.schoolName} between ${result.startDate} and ${result.lastAttendanceDate}.`;
          } else if (!isEmpty(result.startDate)) {
            description += `Currently enrolled at ${result.schoolName} (started on ${result.startDate}).`;
          } else {
            description += `Currently enrolled at ${result.schoolName}.`;
          }
        }
        break;

      // Type Q response is for arrival uploads only.
      case 'Q':
        description = `Verification needed.  ${this.nzMoe!.description}`;
        break;

      // Type O response is for arrival uploads only.
      case 'O':
        description = `Already listed as enrolled at ${
          this.get('school.name') as string
        }.  No further action required.`;
        break;

      case 'J':
        description = 'Uploaded successfully.';
        break;
    }

    return description;
  }

  // statusText provides a brief status message.
  @computed(
    'type',
    'generalErrors.[]',
    'structuralErrors.[]',
    'validationErrors.[]',
    'businessRuleErrors.[]',
  )
  get statusText() {
    switch (this.type) {
      case 'A':
        switch (true) {
          case this.generalErrors.length > 0:
            return 'ENROL is unavailable';
          case this.structuralErrors.length > 0:
            return 'Communication error';
          case this.validationErrors.length > 0:
          case this.businessRuleErrors.length > 0:
            return 'Validation error(s)';
          case this.otherDexErrors.length > 0:
            return 'Error';
        }
        // Shouldn't end up here; we don't know what went wrong.
        return 'Unknown error';

      case 'G':
        return 'NSN not found';

      case 'H':
        return 'Data mismatch';

      // Type I response is for leaver uploads only.
      case 'I':
        return 'Not enrolled';

      // Type P response is for arrival uploads only.
      case 'P':
        return 'First day of attendance is invalid';

      // Type Q response is for arrival uploads only.
      case 'Q':
        return 'Verification needed';

      // Type O response is for arrival uploads only.
      case 'O':
        return 'Already enrolled';

      case 'J':
        return 'Uploaded successfully';
    }

    // Shouldn't end up here either; we don't know what went wrong.
    return `Unknown error`;
  }
}
